import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, FolderOpen, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/projects`, newProject);
      toast.success('Proyecto creado exitosamente');
      setProjectDialogOpen(false);
      setNewProject({ name: '', description: '' });
      fetchData();
      navigate(`/projects/${response.data.id}/editor`);
    } catch (error) {
      toast.error('Error al crear proyecto');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('¿Estás seguro de eliminar este proyecto?')) return;
    try {
      toast.info('Eliminando proyecto...');
      await axios.delete(`${API_URL}/projects/${projectId}`, { timeout: 120000 });
      toast.success('Proyecto eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar proyecto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-sm text-slate-600">Gestiona tus proyectos GTFS</p>
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold tracking-tight">Proyectos GTFS</h2>
          <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-button" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Proyecto
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Proyecto GTFS</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <Label htmlFor="project-name">Nombre</Label>
                  <Input
                    id="project-name"
                    data-testid="project-name-input"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="project-description">Descripción</Label>
                  <Textarea
                    id="project-description"
                    data-testid="project-description-input"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  />
                </div>
                <Button data-testid="submit-project-button" type="submit" className="w-full">Crear</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card key={project.id} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{project.name}</h3>
                    {project.file_stats && Object.keys(project.file_stats).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(project.file_stats).map(([k, v]) => (
                          <span key={k} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                            {k}: {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  data-testid={`delete-project-${project.id}`}
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteProject(project.id)}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <p className="text-sm text-slate-600 mb-4">{project.description || 'Sin descripción'}</p>
              <Button
                data-testid={`open-project-${project.id}`}
                onClick={() => navigate(`/projects/${project.id}/editor`)}
                className="w-full"
                variant="outline"
              >
                Abrir
              </Button>
            </Card>
          ))}
          {projects.length === 0 && (
            <Card className="p-6 col-span-3">
              <p className="text-center text-slate-500">No hay proyectos. Crea uno para comenzar.</p>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
